import { memo } from 'react';
import { Icon, IconProps, Link, Popover, Spinner } from '@amzn/awsui-components-core';
import styles from './ScanFindingsTable.module.scss';
import { ScannerFindingsSummary } from '../../services/ScannerFindings';

export type { ScannerFindingsSummary } from '../../services/ScannerFindings';

/** Alt text shown on hover when the duplication scan failed. */
const DUPLICATION_SCAN_FAILED_MESSAGE = 'Duplication scan failed. Please check manually.';
const SCAN_FAILED_MESSAGE = 'Scan failed. Please check manually.';
const GUARDRAIL_SCAN_FAILED_MESSAGE =
  'This scan was stopped because some content triggered safety controls.';
const SCAN_CANCELLED_MESSAGE = 'Scan cancelled, please scan again before you can continue';

// TODO: get the actual icons from Chris
const SCANNER_ICONS: Record<string, IconProps.Name> = {
  'Tech accuracy & depth': 'security',
  'Best practices': 'script',
};

// Placeholder rows shown before any scan findings exist. Kept neutral (not
// 'failed') so an empty state reads as "no findings yet", not a scan failure.
const DEFAULT_SCANNERS: readonly ScannerFindingsSummary[] = [
  { scannerName: 'Tech accuracy & depth', findingsCount: 0, status: 'success' },
  { scannerName: 'Best practices', findingsCount: 0, status: 'success' },
];

interface ScanFindingsTableProps {
  readonly scannerFindings: readonly ScannerFindingsSummary[];
  readonly isLoading?: boolean;
  readonly onRescan?: () => void;
  readonly isRescanDisabled?: boolean;
  /** Opens the duplication-results modal when the duplication row shows "View". */
  readonly onViewDuplication?: () => void;
  /** When true, failed rows read as "cancelled" instead of "failed". */
  readonly isScanCancelled?: boolean;
}

interface FindingsCellProps {
  readonly row: ScannerFindingsSummary;
  readonly isLoading?: boolean;
  readonly onViewDuplication?: () => void;
  readonly isScanCancelled?: boolean;
}

const FindingsCell = memo(function FindingsCell({
  row,
  isLoading,
  onViewDuplication,
  isScanCancelled,
}: FindingsCellProps) {
  if (isLoading) {
    return <Spinner />;
  }

  // The duplication row is driven by the dedicated duplication-results endpoint.
  if (row.duplicationState) {
    if (row.duplicationState === 'hasResults') {
      return (
        <Link variant="primary" onFollow={onViewDuplication}>
          View
        </Link>
      );
    }
    if (row.duplicationState === 'failed') {
      return (
        <Popover
          triggerType="text"
          renderWithPortal
          dismissButton={false}
          content={DUPLICATION_SCAN_FAILED_MESSAGE}
        >
          <Icon
            name="status-warning"
            variant="warning"
            size="small"
            alt={DUPLICATION_SCAN_FAILED_MESSAGE}
          />
        </Popover>
      );
    }
    return <>0</>;
  }

  // Other scanners show a warning icon on failure, otherwise their count.
  if (row.status === 'failed') {
    const failureMessage = isScanCancelled
      ? SCAN_CANCELLED_MESSAGE
      : row.failureReason === 'guardrail'
        ? GUARDRAIL_SCAN_FAILED_MESSAGE
        : SCAN_FAILED_MESSAGE;
    return (
      <Popover triggerType="text" renderWithPortal dismissButton={false} content={failureMessage}>
        <span role="img" aria-label={failureMessage}>
          <Icon name="status-warning" variant="warning" size="small" />
        </span>
      </Popover>
    );
  }
  return <>{row.findingsCount}</>;
});

/**
 * Custom table because Cloudscape Table has a min-width that overflows
 * the narrow panel (240-360px) and ignores parent font-size overrides.
 */
const ScanFindingsTable = memo(function ScanFindingsTable({
  scannerFindings,
  isLoading,
  onRescan,
  isRescanDisabled,
  onViewDuplication,
  isScanCancelled,
}: ScanFindingsTableProps) {
  const rows = scannerFindings.length > 0 ? scannerFindings : DEFAULT_SCANNERS;

  return (
    <table className={styles.scanTable}>
      <thead>
        <tr>
          <th>
            <span className={styles.scanName}>
              Scan{' '}
              {onRescan && !isRescanDisabled && (
                <button
                  className={styles.rescanButton}
                  onClick={onRescan}
                  aria-label="Rescan content"
                >
                  <Icon name="refresh" size="small" />
                </button>
              )}
            </span>
          </th>
          <th className={styles.findingsCount}>Findings</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.scannerName}>
            <td>
              <span className={styles.scanName}>
                <Icon name={SCANNER_ICONS[row.scannerName] ?? 'status-positive'} size="small" />
                {row.scannerName}
              </span>
            </td>
            <td className={styles.findingsCount}>
              <FindingsCell
                row={row}
                isLoading={isLoading}
                onViewDuplication={onViewDuplication}
                isScanCancelled={isScanCancelled}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
});

export default ScanFindingsTable;
